import React from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, MapPin, Phone, X } from 'lucide-react';
import { CnpjCandidato } from '../../../src/services/descobrirCnpj';
import { maskCnpj } from '../../../src/services/prospeccaoLista';

interface Props {
    isOpen: boolean;
    empresaNome?: string;
    empresaEndereco?: string | null;
    empresaTelefone?: string | null;
    candidatos: CnpjCandidato[];
    cnpjAtual?: string | null;    // CNPJ já vinculado (modo conferir/trocar)
    escolhendo?: string | null;   // cnpj em gravação
    onEscolher: (c: CnpjCandidato) => void;
    onLimpar?: () => void;        // desvincula o CNPJ atual (nenhum candidato corresponde)
    onClose: () => void;
}

const fmtEndereco = (e?: CnpjCandidato['endereco']): string => {
    if (!e) return '';
    const rua = [e.logradouro, e.numero].filter(Boolean).join(', ');
    return [rua, e.bairro, [e.municipio, e.uf].filter(Boolean).join(' - '), e.cep]
        .filter(Boolean).join(' · ');
};

const CnpjCandidatosModal: React.FC<Props> = ({
    isOpen, empresaNome, empresaEndereco, empresaTelefone, candidatos, cnpjAtual, escolhendo, onEscolher, onLimpar, onClose,
}) => {
    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-xl shrink-0">
                            <Building2 size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Qual é a empresa?</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {candidatos.length} candidatos para <span className="font-semibold">{empresaNome}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                {/* Dados do Google p/ comparação */}
                <div className="px-5 py-3 bg-gray-50/70 dark:bg-gray-700/30 border-b border-gray-100 dark:border-gray-700 text-[11px] text-gray-600 dark:text-gray-300 space-y-0.5">
                    {empresaEndereco && <p className="flex items-start gap-1.5"><MapPin size={12} className="mt-0.5 shrink-0 text-gray-400" /> {empresaEndereco}</p>}
                    {empresaTelefone && <p className="flex items-center gap-1.5"><Phone size={12} className="shrink-0 text-gray-400" /> {empresaTelefone}</p>}
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
                    {candidatos.map(c => (
                        <div key={c.cnpj} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${cnpjAtual === c.cnpj ? 'border-violet-400 dark:border-violet-500 bg-violet-50/50 dark:bg-violet-900/10' : 'border-gray-200 dark:border-gray-700 hover:border-violet-300 dark:hover:border-violet-600'}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{c.razao_social}</p>
                                    {cnpjAtual === c.cnpj && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">vinculado</span>
                                    )}
                                    {c.telefone_bateu && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">telefone bateu</span>
                                    )}
                                    {c.endereco_bateu && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">endereço bateu</span>
                                    )}
                                    {c.cidade_bateu === false && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">outra cidade</span>
                                    )}
                                    {c.matriz_filial && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{c.matriz_filial}</span>
                                    )}
                                    {c.situacao_cadastral && c.situacao_cadastral !== 'ATIVA' && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{c.situacao_cadastral}</span>
                                    )}
                                </div>
                                {c.nome_fantasia && <p className="text-[11px] text-gray-500 dark:text-gray-400">Fantasia: {c.nome_fantasia}</p>}
                                <p className="text-[11px] font-mono text-gray-600 dark:text-gray-300 mt-0.5">{maskCnpj(c.cnpj)}</p>
                                {fmtEndereco(c.endereco) && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{fmtEndereco(c.endereco)}</p>}
                                {c.atividade_principal?.descricao && (
                                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">CNAE: {c.atividade_principal.descricao}</p>
                                )}
                            </div>
                            {cnpjAtual !== c.cnpj && (
                                <button
                                    onClick={() => onEscolher(c)}
                                    disabled={!!escolhendo}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-violet-600 text-white hover:opacity-90 transition-opacity disabled:opacity-40 shrink-0"
                                >
                                    <Check size={12} /> {escolhendo === c.cnpj ? 'Salvando...' : 'É esta'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
                    {onLimpar && (
                        <button onClick={onLimpar} className="px-4 py-1.5 text-xs font-semibold rounded-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            Nenhuma corresponde — desvincular CNPJ
                        </button>
                    )}
                    <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                        {onLimpar ? 'Fechar' : 'Nenhuma corresponde'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CnpjCandidatosModal;
